import Head from "next/head";
import { useRouter } from "next/router";

import { SELECTION_PATH_PATTERN } from "../../../config/selection-paths";
import { APP_STORE_ID } from "@/common/static/apps.static";
import { cleanSearchParams } from "@/common/utils/router.utils";
import { withLocale } from "@/common/utils/url.utils";
import { CANONICAL_HOST } from "@/config";
import { removeTrailingSlash } from "@/utils/removeTrailingSlash";

/**
 * The URL Safari hands the iOS app when the visitor taps the Smart App Banner
 * (WHO-5649). It is the current page on the canonical host, locale prefix
 * included, so the app's Universal Link handling opens the same product,
 * search or profile instead of Home. Tracking-only params Next leaks into the
 * URL are dropped; anything else stays because the app may read it.
 *
 * `null` while a fallback page still renders its route pattern (`/products/[slug]`):
 * that is not a URL the app can open, so the banner falls back to Home. Only the
 * path is checked; a bracket inside a query value is a legitimate URL.
 *
 * Apple parses the meta content as a comma-separated list, so a comma inside
 * the URL is percent-encoded to keep the argument in one piece.
 */
export const getSmartAppBannerArgument = (
  asPath: string,
  locale?: string,
  defaultLocale?: string
): string | null => {
  const url = new URL(
    withLocale(asPath, locale, defaultLocale),
    CANONICAL_HOST
  );

  if (url.pathname.includes("[")) {
    return null;
  }

  cleanSearchParams(url.searchParams);

  return removeTrailingSlash(url.href).replace(/,/g, "%2C");
};

export const getSmartAppBannerContent = (argument: string | null) =>
  argument
    ? `app-id=${APP_STORE_ID}, app-argument=${argument}`
    : `app-id=${APP_STORE_ID}`;

/**
 * Safari's Smart App Banner for the iOS app.
 *
 * This and `AppBanner` in this folder are two banners that never appear
 * together. Only Mobile Safari renders `apple-itunes-app`, and
 * `shouldShowAppBanner` returns false for exactly that browser, so Mobile
 * Safari gets Apple's banner and every other mobile browser gets ours. Change
 * either gate and mobile Safari users can end up with both.
 *
 * Rendered from `_app`, not `_document`, because the `app-argument` needs the
 * current URL and `_document` has no router. Safari reads the tag once per
 * page load, so the argument reflects the page the visitor landed on; a
 * client-side navigation updates the tag but the banner keeps its first
 * target until the next full load. `next/head` de-duplicates on the `key`.
 */
export const SmartAppBanner = () => {
  const { asPath, locale, defaultLocale } = useRouter();
  const argument = getSmartAppBannerArgument(asPath, locale, defaultLocale);

  /* WHO-6629: no app banner on a Pro Buyer's client link, whatever its locale and word (WHO-6641). */
  if (SELECTION_PATH_PATTERN.test(asPath)) {
    return null;
  }

  return (
    <Head>
      <meta
        key="apple-itunes-app"
        name="apple-itunes-app"
        content={getSmartAppBannerContent(argument)}
      />
    </Head>
  );
};
